import * as React from "react";
import submitAnswers, { SubmitDataDTO } from "../../services/answers";
import { QuestionDTO } from "../../services/questions";
import { PageType, ChildProps } from "../main/main";
import QuestionComponent, { Question as QuestionType } from "../question/question";

import styles from "./questions.module.scss";

import { ReactComponent as ArrowRight } from "../../style/assets/arrow-right.svg";

const Questions = (props: ChildProps & { questions: QuestionDTO[] }) => {
    const { questions, setFooter, setPage } = props;
    const [questionNumber, setQuestionNumber] = React.useState(0);
    const [selectedAnswer, setSelectedAnswer] = React.useState("");
    const [showResult, setShowResult] = React.useState(false);

    const questionsTotal = questions.length;
    const currentQuestion = questions[questionNumber];

    React.useEffect(() => {
        window.onbeforeunload = () => window.confirm();

        if (selectedAnswer === "") {
            setFooter({ Button: undefined });
            return;
        }
        const onClick = async () => {
            setShowResult(true);
            saveAnswer(currentQuestion, selectedAnswer);
            if (questionNumber < questionsTotal - 1) {
                const onClick = () => {
                    setQuestionNumber(questionNumber + 1);
                    setSelectedAnswer("");
                    setShowResult(false);
                };
                setFooter({
                    Button: (
                        <button className={"quiz_button"} onClick={onClick}>
                            Next question
                            <ArrowRight className={"arrow"} title="Next question" />
                        </button>
                    ),
                });
            } else {
                const onClick = async () => {
                    const surveyData = sessionStorage.getItem("surveyData");
                    const allAnswers = sessionStorage.getItem("answers");
                    if (surveyData && allAnswers) {
                        const data: SubmitDataDTO = {
                            surveyData: JSON.parse(surveyData),
                            answers: JSON.parse(allAnswers),
                        };
                        await submitAnswers(data);
                        setPage(PageType.result);
                    }
                };
                setFooter({
                    Button: (
                        <button className={"quiz_button"} onClick={onClick}>
                            Go to results
                            <ArrowRight className={"arrow"} title="Go to results" />
                        </button>
                    ),
                });
            }
        };
        setFooter({
            Button: (
                <button className={"quiz_button"} data-empty-button={true} onClick={onClick}>
                    Check answer
                </button>
            ),
        });
    }, [questionNumber, currentQuestion, questionsTotal, selectedAnswer, setFooter, setPage]);

    if (!questions || questions.length === 0) {
        return (
            <div className={styles.loadingText}>
                Please wait a moment while the quiz is loading...
            </div>
        );
    }

    const correctOption = currentQuestion.options.find(x => x.type === "Answer");
    const q: QuestionType = {
        id: currentQuestion.id,
        options: currentQuestion.options.map(x => x.option),
        correctOption: correctOption?.option,
        style: "radio",
    };

    const onSelectOption = (answer: any) => {
        setSelectedAnswer(answer[currentQuestion.id]);
    };

    return (
        <div>
            <ProgressBar questionNumber={questionNumber} total={questionsTotal} />
            <div className={styles.questionText}>{currentQuestion.question}</div>
            {currentQuestion.image && (
                <div className={styles.imageContainer}>
                    <img
                        className={styles.questionImage}
                        src={`data:image/jpeg;base64,${currentQuestion.image}`}
                        alt="Question illustration"
                    />
                </div>
            )}
            <QuestionComponent
                question={q}
                selectedAnswer={selectedAnswer}
                setAnswer={onSelectOption}
                showCorrectOption={showResult}
                disabled={showResult}
            />
        </div>
    );
};

const saveAnswer = (currentQuestion: QuestionDTO, selectedAnswer: string) => {
    const ans = currentQuestion.options.find(x => x.option === selectedAnswer);
    if (ans) {
        const tmp = {
            questionId: currentQuestion.id,
            type: ans.type,
        };
        const json = sessionStorage.getItem("answers");
        if (!json) {
            sessionStorage.setItem("answers", JSON.stringify([tmp]));
        } else {
            const allAnswers = JSON.parse(json);
            allAnswers.push(tmp);
            sessionStorage.setItem("answers", JSON.stringify(allAnswers));
        }
    }
};

const ProgressBar = (props: { questionNumber: number; total: number }) => {
    const { questionNumber, total } = props;
    return (
        <div>
            <div className={styles.progressBarText}>
                <span className={styles.largeNumber}>{questionNumber + 1}</span>
                {` out of ${total}`}
            </div>
            <div className={styles.bar}>
                <div
                    className={styles.filledBar}
                    style={{ width: `calc(${100 / total}% * ${questionNumber + 1})` }}
                />
            </div>
        </div>
    );
};

export default Questions;
